import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { cmsApi } from "@api";
import { CmsHomepage, CmsMenu } from "@interfaces";
import { RootState } from ".";

interface ICmsState {
    homepage: CmsHomepage | null;
    headerMenu: CmsMenu | null;
    footerMenu: CmsMenu | null;
    homepageStatus: "idle" | "loading" | "succeeded" | "failed";
    menusStatus: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
}

const initialState: ICmsState = {
    homepage: null,
    headerMenu: null,
    footerMenu: null,
    homepageStatus: "idle",
    menusStatus: "idle",
    error: null,
};

export const fetchCmsHomepage = createAsyncThunk<CmsHomepage, void, { rejectValue: string }>(
    "cms/fetchHomepage",
    async (_, thunkAPI) => {
        try {
            return await cmsApi.getHomepage();
        } catch (err) {
            console.error("[cms] fetchHomepage failed", err);
            return thunkAPI.rejectWithValue("Failed to load homepage content");
        }
    },
);

export const fetchCmsMenus = createAsyncThunk<
    { header: CmsMenu; footer: CmsMenu },
    void,
    { rejectValue: string }
>("cms/fetchMenus", async (_, thunkAPI) => {
    try {
        const [header, footer] = await Promise.all([
            cmsApi.getMenu("header"),
            cmsApi.getMenu("footer"),
        ]);
        return { header, footer };
    } catch (err) {
        console.error("[cms] fetchMenus failed", err);
        return thunkAPI.rejectWithValue("Failed to load menus");
    }
});

// Menu links hidden from the site header/footer until their pages have real
// content. The pages themselves stay routable (they are still reachable from
// the CMS showcase pages); only the main navigation entries are suppressed.
// Remove an href from this list once its page is content-complete.
export const HIDDEN_MENU_HREFS = ["/search-result", "/blog", "/about-us"];

export const filterLaunchReadyMenuItems = (menu: CmsMenu): CmsMenu => ({
    ...menu,
    items: (menu.items ?? []).filter((item) => !HIDDEN_MENU_HREFS.includes(item.href)),
});

const cmsSlice = createSlice({
    name: "cms",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchCmsHomepage.pending, (state) => {
                state.homepageStatus = "loading";
            })
            .addCase(fetchCmsHomepage.fulfilled, (state, action) => {
                state.homepageStatus = "succeeded";
                state.homepage = action.payload;
            })
            .addCase(fetchCmsHomepage.rejected, (state, action) => {
                state.homepageStatus = "failed";
                state.error = action.payload ?? "Failed to load homepage content";
            })
            .addCase(fetchCmsMenus.pending, (state) => {
                state.menusStatus = "loading";
            })
            .addCase(fetchCmsMenus.fulfilled, (state, action) => {
                state.menusStatus = "succeeded";
                state.headerMenu = filterLaunchReadyMenuItems(action.payload.header);
                state.footerMenu = filterLaunchReadyMenuItems(action.payload.footer);
            })
            .addCase(fetchCmsMenus.rejected, (state, action) => {
                state.menusStatus = "failed";
                state.error = action.payload ?? "Failed to load menus";
            });
    },
});

export const selectCms = (state: RootState) => state.cms;
export const selectCmsHomepage = (state: RootState) => state.cms.homepage;
export const selectCmsHeaderMenu = (state: RootState) => state.cms.headerMenu;
export const selectCmsFooterMenu = (state: RootState) => state.cms.footerMenu;

export default cmsSlice.reducer;
